import { GenerateABDFDTData } from "@/api/generate-abd-fdt";
import { MediaData } from "@/api/media";
import {
  OPMFDTData,
  useInReviewValidateFDT,
  useOPMFDTGenerate,
  useOPMFDTs,
} from "@/api/opm-fdt";
import {
  OPMFDTValidationSchema,
  useOPMFDTValidate,
  useRFS,
  useRFSStatusUpdate,
} from "@/api/rfs";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2Icon,
  CheckSquare2,
  LucideUpload,
  XCircleIcon,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { ROLE, STATUS } from "@/utils/constant";
import { swalErrorDefault, swalLoading, swalSuccess } from "@/utils/lib/swal";
import axios from "axios";
import { FormContainer } from "@/components/form/form-container";
import { ImageList, ImageListItem, ImageListItemBar } from "@mui/material";
import { FieldFile } from "@/components/form/field-file";
import { DefaultButton } from "@/components/ui/button";
import { ImageWithPreview } from "./ImageWithPreview";
import {
  QueryObserverResult,
  RefetchOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useImageCompressor } from "@/utils/hook/useImageCompressor";
import { useValidation } from "./contexts/validationContext";
import { createRFSStatusUpdater } from "./utils/rfsStatusUpdater";
import {
  FDT_ERROR_FIELD_TO_STATUS,
  overrideFdtInvalid,
  overrideFdtValid,
  statusFieldsFromErrors,
} from "./utils/dataMappers";
import { OverallStatus } from "./providers/ValidationProvider";
import { useAllowEditOuputFdt } from "@/api/master-param";
import { H5, P } from "@/components/ui/typography";
import RegenerateSection from "../rfs/RegenerateSection";

interface FDTAccordionItemsProps {
  abdFdts?: GenerateABDFDTData[];
  opmFdts?: OPMFDTData[];
  id: string;
  refetchOpmFdts: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<OPMFDTData[], Error>>;
}
export function FDTAccordionItems({
  abdFdts,
  opmFdts,
  id,
  refetchOpmFdts,
}: FDTAccordionItemsProps) {
  const { data: allowEditOutputFdts } = useAllowEditOuputFdt();
  const isEditFdt = allowEditOutputFdts?.content[0]?.active;
  if (!abdFdts || !opmFdts) {
    return null;
  }
  return (
    <>
      {abdFdts.map((abdFdt, index) => (
        <AccordionItem value={abdFdt.id} key={abdFdt.id}>
          <AccordionTrigger>
            <span className="flex gap-2 dark:text-neutral-300">
              FDT ID: {abdFdt?.fdtId}
              {opmFdts?.[index]?.valid === true && (
                <CheckSquare2 color="#5dee7a" />
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent className="flex gap-x-2">
            {opmFdts[index] ? (
              <FDTForm
                opmFdt={opmFdts[index]}
                opmFdts={opmFdts}
                index={index}
                rfsId={id}
                fdtId={abdFdt.fdtId}
                isEditFdt={isEditFdt}
                refetchOpmFdts={refetchOpmFdts}
              />
            ) : (
              <div className="flex flex-col gap-y-2">
                <H5>Data Not Available</H5>
                <P>
                  OPM FDT data has not been generated yet for this FDT. Please
                  regenerate the data and try again.
                </P>
                <RegenerateSection />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </>
  );
}
function FDTForm({
  opmFdt,
  opmFdts,
  index,
  rfsId,
  fdtId,
  isEditFdt,
  refetchOpmFdts,
}: {
  opmFdt: OPMFDTData;
  opmFdts: OPMFDTData[];
  index: number;
  rfsId: string;
  fdtId: string;
  isEditFdt?: boolean;
  refetchOpmFdts: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<OPMFDTData[], Error>>;
}) {
  const [poleFdtMedia, setPoleFdtMedia] = useState<MediaData>(
    opmFdt?.poleFdtMedia,
  );
  const [photoFdtMedia, setPhotoFdtMedia] = useState<MediaData>(
    opmFdt?.imageFdtMedia,
  );
  const [outputFdtMedia, setOutputFdtMedia] = useState<MediaData>(
    opmFdt?.outputFdtMedia,
  );
  const [inputFdtMedia, setInputFdtMedia] = useState<MediaData>(
    opmFdt?.inputFdtMedia,
  );
  const [splitterFdtMedia, setSplitterFdtMedia] = useState<MediaData>(
    opmFdt?.splitterFdtMedia,
  );

  // OCR Result State
  const [photoFdtResult, setPhotoFdtResult] = useState<string>(
    opmFdt?.fdtIdText,
  );
  const [outputFdtResult, setOutputFdtResult] = useState<string>(
    opmFdt?.outputFdtText,
  );
  const [inputFdtResult, setInputFdtResult] = useState<string>(
    opmFdt?.inputFdtText,
  );

  const queryClient = useQueryClient();
  const { isFetching: isOpmFdtsLoading } = useOPMFDTs({ rfsId: rfsId });

  const form = useForm({
    mode: "onChange",
  });

  const currentPoleFdt = form.watch("tiang_fdt");
  const currentImageFdt = form.watch("foto_fdt");
  const currentOutputFdt = form.watch("output_fdt");
  const currentInputFdt = form.watch("input_fdt");
  const currentSplitterFdt = form.watch("splitter_fdt");
  const poleFdtPath = currentPoleFdt?.stream || opmFdt?.poleFdtMedia?.stream;
  const imageFdtPath = currentImageFdt?.stream || opmFdt?.imageFdtMedia?.stream;
  const outputFdtPath =
    currentOutputFdt?.stream || opmFdt?.outputFdtMedia?.stream;
  const inputFdtPath = currentInputFdt?.stream || opmFdt?.inputFdtMedia?.stream;
  const splitterFdtPath =
    currentSplitterFdt?.stream || opmFdt?.splitterFdtMedia?.stream;
  const hasAllImages =
    poleFdtPath &&
    imageFdtPath &&
    outputFdtPath &&
    inputFdtPath &&
    splitterFdtPath;

  const { overallStatus: currentOverallStatus, getStatusForSubmit } =
    useValidation();
  const { mutateAsync: compress } = useImageCompressor();

  const { data: rfs } = useRFS({ id: rfsId });
  const { mutate: updateStatus } = useRFSStatusUpdate();
  const { mutate: submit, isPending: isSubmitting } = useOPMFDTValidate();
  const { mutate: update, isPending: isUpdating } = useInReviewValidateFDT();
  const { mutate: generate, isPending: isGenerating } = useOPMFDTGenerate();

  const { roles } = useCurrentUser();
  const canReview = hasCommonElement(roles, [
    ROLE.ADMINISTRATOR,
    ROLE.REVISE_DOCUMENT_OFFICER,
    ROLE.VENDOR,
    ROLE.VALIDATOR_DOCUMENT_OFFICER,
  ]);
  const isViewOnly = hasCommonElement(roles, [ROLE.VENDOR_VIEW]);

  const isReview = rfs?.statusRfsSubmission === STATUS.RFS.REVISION_BY_LN;
  const isReadonly =
    rfs?.statusRfsSubmission === STATUS.RFS.GENERATING_HPDB || isViewOnly;
  const isOcrNewInput =
    photoFdtResult !== (opmFdt?.fdtIdText ?? "") ||
    outputFdtResult !== (opmFdt?.outputFdtText ?? "") ||
    inputFdtResult !== (opmFdt?.inputFdtText ?? "");

  const disableGenerateOCRButton =
    !hasAllImages || !canReview || isReadonly || isReview;
  const disableSubmitButton =
    !hasAllImages ||
    !photoFdtResult ||
    !outputFdtResult ||
    !inputFdtResult ||
    isGenerating ||
    (isReview && !isOcrNewInput) || // disable submit button ketika user dalam proses review dan tidak melakukan perubahan pada OCR output text atau fat id
    !canReview ||
    isOpmFdtsLoading ||
    isReadonly;

  const acceptedExtensions = ".jpg, .jpeg";

  function handleStatus(nextStatus: OverallStatus) {
    const statusUpdater = createRFSStatusUpdater({
      rfsId,
      overallStatus: nextStatus,
      rfsData: rfs,
      updateStatusMutation: updateStatus,
      prefix: "opm",
      options: {
        onError: () => {
          swalErrorDefault.fire({
            title: "Status Update Failed",
            text: "You failed to update the RFS status. Please try again.",
          });
        },
      },
    });
    statusUpdater();
  }

  function handleFileDragOver(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
  }

  function handleFileDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();

    const files = e.dataTransfer.files;
    if (!files.length) {
      return;
    }
    if (files.length > 1) {
      throw new Error("Only one file is allowed");
    }

    const changeEvent = new Event("change", { bubbles: true });
    const input = e.currentTarget.querySelector("input");
    if (!input) {
      return;
    }
    input.files = files;
    input.dispatchEvent(changeEvent);
  }

  function handleGenerateOCR() {
    swalLoading.fire();

    generate(
      {
        imageFdtMedia: { path: photoFdtMedia.path },
        inputFdtMedia: { path: inputFdtMedia.path },
        outputFdtMedia: { path: outputFdtMedia.path },
        fdtId: fdtId,
      },
      {
        onSuccess: (response) => {
          setPhotoFdtResult(response.fdtIdText);
          setInputFdtResult(response.inputFdtText);
          setOutputFdtResult(response.outputFdtText);
          swalLoading.close();
        },
        onError: () => {
          setPhotoFdtResult("");
          setOutputFdtResult("");
          setInputFdtResult("");
          swalErrorDefault.fire({
            title: "OCR Generation Failed",
            text: "You failed to generate OCR results. Please try again.",
          });
        },
      },
    );
  }

  function handleSubmit() {
    if (
      !poleFdtMedia ||
      !photoFdtMedia ||
      !outputFdtMedia ||
      !inputFdtMedia ||
      !splitterFdtMedia ||
      !photoFdtResult ||
      !outputFdtResult ||
      !inputFdtResult
    ) {
      console.error("Missing required data: ", {
        poleFdtMedia,
        photoFdtMedia,
        outputFdtMedia,
        inputFdtMedia,
        splitterFdtMedia,
        photoFdtResult,
        outputFdtResult,
        inputFdtResult,
      });
      return;
    }
    const submitAction = isReview ? update : submit;

    swalLoading.fire();
    const data: OPMFDTValidationSchema = {
      opmFdt: {
        fdtId: fdtId,
        fdtIdText: photoFdtResult,
        inputFdtText: inputFdtResult,
        outputFdtText: outputFdtResult,
        poleFdtMedia: {
          id: poleFdtMedia.id,
          path: poleFdtMedia.path,
          originalFileName: poleFdtMedia.originalFileName,
        },
        imageFdtMedia: {
          id: photoFdtMedia.id,
          path: photoFdtMedia.path,
          originalFileName: photoFdtMedia.originalFileName,
        },
        outputFdtMedia: {
          id: outputFdtMedia.id,
          path: outputFdtMedia.path,
          originalFileName: outputFdtMedia.originalFileName,
        },
        inputFdtMedia: {
          id: inputFdtMedia.id,
          path: inputFdtMedia.path,
          originalFileName: inputFdtMedia.originalFileName,
        },
        splitterFdtMedia: {
          id: splitterFdtMedia.id,
          path: splitterFdtMedia.path,
          originalFileName: splitterFdtMedia.originalFileName,
        },
      },
    };
    if (opmFdt?.id) {
      data.opmFdt.id = opmFdt.id;
    }
    submitAction(
      {
        id: rfsId,
        data: data,
      },
      {
        onSuccess: () => {
          refetchOpmFdts();

          form.clearErrors();
          swalSuccess.fire({
            title: "Validation Successful",
            text: "You have successfully submitted an FDT.",
          });

          // refetchOpmFdts() di atas asinkron dan tidak ditunggu, jadi hitung
          // status dari data yang sudah di-override.
          const nextStatus =
            getStatusForSubmit?.({
              opmFdts: overrideFdtValid(opmFdts, index),
            })?.overallStatus ?? currentOverallStatus;
          handleStatus(nextStatus);
        },
        onError: (error) => {
          form.clearErrors();
          if (axios.isAxiosError(error)) {
            const messages: { [key: string]: string }[] =
              error.response?.data?.data ?? [];

            let poleFdtMessage = "";
            let imageFdtMessage = "";
            let outputFdtMessage = "";
            let inputFdtMessage = "";
            let splitterFdtMessage = "";
            let fdtIdTextMessage = "";
            let inputFdtTextMessage = "";
            let outputFdtTextMessage = "";

            for (const message of messages) {
              if (message.poleFdtMedia) {
                poleFdtMessage += message.poleFdtMedia;
              }
              if (message.imageFdtMedia) {
                imageFdtMessage += message.imageFdtMedia + ". ";
              }
              if (message.fdtIdText) {
                fdtIdTextMessage += message.fdtIdText + ". ";
              }
              if (message.outputFdtMedia) {
                outputFdtMessage += message.outputFdtMedia + ". ";
              }
              if (message.outputFdtText) {
                outputFdtTextMessage += message.outputFdtText + ". ";
              }
              if (message.inputFdtMedia) {
                inputFdtMessage += message.inputFdtMedia + ". ";
              }
              if (message.inputFdtText) {
                inputFdtTextMessage += message.inputFdtText + ". ";
              }
              if (message.splitterFdtMedia) {
                splitterFdtMessage += message.splitterFdtMedia;
              }
            }

            form.setError("tiang_fdt", {
              message: poleFdtMessage,
            });
            form.setError("foto_fdt", {
              message: `${imageFdtMessage}${fdtIdTextMessage}`,
            });
            form.setError("output_fdt", {
              message: `${outputFdtMessage}${outputFdtTextMessage}`,
            });
            form.setError("input_fdt", {
              message: `${inputFdtMessage}${inputFdtTextMessage}`,
            });
            form.setError("splitter_fdt", {
              message: splitterFdtMessage,
            });

            // Submit ditolak: cermin dari onSuccess — tambal data secara lokal lalu
            // hitung ulang, tanpa menunggu refetch. onSettled yang menyegarkan UI.
            if (error.response?.status === 400) {
              const failedFields = statusFieldsFromErrors<OPMFDTData>(
                error.response?.data?.data,
                FDT_ERROR_FIELD_TO_STATUS,
              );
              const nextStatus =
                getStatusForSubmit?.({
                  opmFdts: overrideFdtInvalid(opmFdts, index, failedFields),
                })?.overallStatus ?? currentOverallStatus;
              handleStatus(nextStatus);
            }
          }
          swalErrorDefault.fire({
            title: "Validation Failed",
            text: "You failed to perform data validation. Please check the data then try again.",
          });
        },
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: ["opmFdts", { rfsId }],
          });
        },
      },
    );
  }

  return (
    <>
      <FormContainer form={form} className="w-full">
        <ImageList cols={5} rowHeight={240} className="flex-grow">
          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  FDT Pole <ImageStatusIcon valid={opmFdt?.poleFdtStatus} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />
            {(currentPoleFdt?.path || opmFdt?.poleFdt) && (
              <ImageWithPreview src={poleFdtPath} alt="Tiang FDT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.tiang_fdt && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.tiang_fdt?.message?.toString()}
                </span>
              )}
              <label
                className={`flex cursor-pointer flex-col items-center border-2 border-dashed py-2 text-neutral-600 dark:text-neutral-300 ${
                  isReadonly && "sr-only"
                }`}
                onDragOver={handleFileDragOver}
                onDrop={handleFileDrop}
              >
                <LucideUpload />
                click or drag here
                <FieldFile
                  isVisuallyHidden
                  name="tiang_fdt"
                  label="Tiang FDT"
                  media={{
                    target: "/opm/pole",
                    after: (result) => setPoleFdtMedia(result),
                    before: (files) => {
                      const compressedFile = compress(files[0]);
                      return compressedFile;
                    },
                  }}
                  accept={acceptedExtensions}
                />
              </label>
            </div>
          </ImageListItem>
          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  FDT Photo <ImageStatusIcon valid={opmFdt?.imageFdtStatus} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />

            {(currentImageFdt?.path || opmFdt?.imageFdt) && (
              <ImageWithPreview src={imageFdtPath} alt="Foto FDT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.foto_fdt && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.foto_fdt?.message?.toString()}
                </span>
              )}
              <input
                className="w-full border-2 text-neutral-950"
                value={photoFdtResult}
                onChange={(e) => {
                  setPhotoFdtResult(e.target.value);
                }}
                disabled={!isEditFdt}
              />
              <label
                className={`flex cursor-pointer flex-col items-center border-2 border-dashed py-2 text-neutral-600 dark:text-neutral-300 ${
                  isReadonly && "sr-only"
                }`}
                onDragOver={handleFileDragOver}
                onDrop={handleFileDrop}
              >
                <LucideUpload />
                click or drag here
                <FieldFile
                  isVisuallyHidden
                  name="foto_fdt"
                  label="Foto FDT"
                  media={{
                    target: "/opm/photo",
                    after: (result) => {
                      setPhotoFdtMedia(result);
                      setPhotoFdtResult("");
                    },
                    before: (files) => {
                      const compressedFile = compress(files[0]);
                      return compressedFile;
                    },
                  }}
                  accept={acceptedExtensions}
                />
              </label>
            </div>
          </ImageListItem>
          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  Splitter FDT Photo{" "}
                  <ImageStatusIcon valid={opmFdt?.splitterFdtStatus} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />

            {(currentSplitterFdt?.path || opmFdt?.splitterFdt) && (
              <ImageWithPreview src={splitterFdtPath} alt="Splitter FDT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.splitter_fdt && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.splitter_fdt?.message?.toString()}
                </span>
              )}
              <label
                className={`flex cursor-pointer flex-col items-center border-2 border-dashed py-2 text-neutral-600 dark:text-neutral-300 ${
                  isReadonly && "sr-only"
                }`}
                onDragOver={handleFileDragOver}
                onDrop={handleFileDrop}
              >
                <LucideUpload />
                click or drag here
                <FieldFile
                  isVisuallyHidden
                  name="splitter_fdt"
                  label="Splitter FDT"
                  media={{
                    target: "/opm/photo",
                    after: (result) => {
                      setSplitterFdtMedia(result);
                    },
                    before: (files) => {
                      const compressedFile = compress(files[0]);
                      return compressedFile;
                    },
                  }}
                  accept={acceptedExtensions}
                />
              </label>
            </div>
          </ImageListItem>
          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  FDT Output <ImageStatusIcon valid={opmFdt?.outputFdtStatus} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />
            {(currentOutputFdt?.path || opmFdt?.outputFdt) && (
              <ImageWithPreview src={outputFdtPath} alt="Output FDT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.output_fdt && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.output_fdt?.message?.toString()}
                </span>
              )}
              <input
                className="w-full border-2 text-neutral-950"
                value={outputFdtResult}
                onChange={(e) => {
                  const lastData = (
                    e.nativeEvent as unknown as { data: string | null }
                  ).data;
                  if (lastData && !/[\d.-]/.test(lastData)) {
                    return;
                  }
                  setOutputFdtResult(e.target.value);
                }}
                disabled={!isEditFdt}
              />
              <label
                className={`flex cursor-pointer flex-col items-center border-2 border-dashed py-2 text-neutral-600 dark:text-neutral-300 ${
                  isReadonly && "sr-only"
                }`}
                onDragOver={handleFileDragOver}
                onDrop={handleFileDrop}
              >
                <LucideUpload />
                click or drag here
                <FieldFile
                  isVisuallyHidden
                  name="output_fdt"
                  label="Output FDT"
                  media={{
                    target: "/opm/output",
                    after: (result) => {
                      setOutputFdtMedia(result);
                      setOutputFdtResult("");
                    },
                    before: (files) => {
                      const compressedFile = compress(files[0]);
                      return compressedFile;
                    },
                  }}
                  accept={acceptedExtensions}
                />
              </label>
            </div>
          </ImageListItem>
          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  FDT Input <ImageStatusIcon valid={opmFdt?.inputFdtStatus} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />
            {(currentInputFdt?.path || opmFdt?.inputFdt) && (
              <ImageWithPreview src={inputFdtPath} alt="Input FDT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.input_fdt && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.input_fdt?.message?.toString()}
                </span>
              )}
              <input
                className="w-full border-2 text-neutral-950"
                value={inputFdtResult}
                onChange={(e) => {
                  const lastData = (
                    e.nativeEvent as unknown as { data: string | null }
                  ).data;
                  if (lastData && !/[\d.-]/.test(lastData)) {
                    return;
                  }
                  setInputFdtResult(e.target.value);
                }}
                disabled={!isEditFdt}
              />
              <label
                className={`flex cursor-pointer flex-col items-center border-2 border-dashed py-2 text-neutral-600 dark:text-neutral-300 ${
                  isReadonly && "sr-only"
                }`}
                onDragOver={handleFileDragOver}
                onDrop={handleFileDrop}
              >
                <LucideUpload />
                click or drag here
                <FieldFile
                  isVisuallyHidden
                  name="input_fdt"
                  label="Input FDT"
                  media={{
                    target: "/opm/input",
                    after: (result) => {
                      setInputFdtMedia(result);
                      setInputFdtResult("");
                    },
                    before: (files) => {
                      const compressedFile = compress(files[0]);
                      return compressedFile;
                    },
                  }}
                  accept={acceptedExtensions}
                />
              </label>
            </div>
          </ImageListItem>
        </ImageList>
      </FormContainer>
      <div className="flex w-24 flex-col justify-center gap-y-2">
        <DefaultButton
          type="button"
          mood="axiata-blue-outline"
          className="w-full"
          disabled={disableGenerateOCRButton}
          loading={isGenerating}
          onClick={handleGenerateOCR}
        >
          Generate
        </DefaultButton>
        <DefaultButton
          type="button"
          mood="axiata-blue"
          className="w-full"
          disabled={disableSubmitButton}
          loading={isUpdating || isSubmitting || isOpmFdtsLoading}
          onClick={handleSubmit}
          title={isReview ? "In the review process" : undefined}
        >
          Submit
        </DefaultButton>
      </div>
    </>
  );
}

function ImageStatusIcon({ valid }: { valid: boolean | null }) {
  if (valid === null || valid === undefined) {
    return null;
  }
  if (valid === true) {
    return <CheckCircle2Icon size={16} className="text-green-500" />;
  }
  return <XCircleIcon size={16} className="text-red-500" />;
}
